import React, { useState, useEffect, useCallback, useRef } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Crown, Plus, Edit, Save, Trash2, Eye, EyeOff, CheckCircle2, X, Loader2,
  DollarSign, Package, Users, HardDrive, Percent, Clock, Zap, AlertCircle, Building2,
  FileUp, FileDown
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import CustomPlansTab from './CustomPlansTab';
import TrialSettingsTab from './TrialSettingsTab';

// Feature row definition for comparison table
const FEATURE_GROUPS = [
  {
    group: 'Pricing',
    icon: DollarSign,
    features: [
      { key: 'monthly_price', label: 'Monthly Price (₹)', type: 'currency', editable: true },
      { key: 'annual_price', label: 'Annual Price (₹)', type: 'currency', editable: true },
      { key: 'gst_rate', label: 'GST Rate (%)', type: 'number', editable: true, suffix: '%' },
      { key: 'is_custom_pricing', label: 'Custom Pricing', type: 'boolean', editable: true },
    ]
  },
  {
    group: 'Commission & Leads',
    icon: Percent,
    features: [
      { key: 'commission_percentage', label: 'Commission Rate (%)', type: 'number', editable: true, suffix: '%' },
      { key: 'lead_base_price', label: 'Lead Base Price (₹)', type: 'currency', editable: true },
      { key: 'leads_ad_credits_percentage', label: 'Ad Credits (%)', type: 'number', editable: true, suffix: '%' },
    ]
  },
  {
    group: 'Listings',
    icon: Package,
    features: [
      { key: 'max_listings', label: 'Listings Limit', type: 'number', editable: true, customText: { '-1': 'Custom' } },
      { key: 'listings_pricing_type', label: 'Listings Pricing', type: 'select', options: ['custom', 'fixed', 'unlimited'], editable: true },
      { key: 'extra_listing_price', label: 'Extra Listing (₹)', type: 'currency', editable: true },
      { key: 'listing_types_included', label: 'Listing Types Included', type: 'number', editable: true },
      { key: 'extra_listing_type_price', label: 'Extra Listing Type (₹/mo)', type: 'currency', editable: true },
    ]
  },
  {
    group: 'Storage',
    icon: HardDrive,
    features: [
      { key: 'media_storage_mb', label: 'Storage (MB)', type: 'number', editable: true, customText: { '-1': 'Custom' } },
      { key: 'extra_storage_price_per_gb', label: 'Extra Storage (₹/GB)', type: 'currency', editable: true },
    ]
  },
  {
    group: 'Team & Locations',
    icon: Users,
    features: [
      { key: 'team_members_included', label: 'Team Members Included', type: 'number', editable: true },
      { key: 'team_members_max', label: 'Max Team Members', type: 'number', editable: true },
      { key: 'extra_team_member_price', label: 'Extra Member (₹/mo)', type: 'currency', editable: true },
      { key: 'max_locations', label: 'Locations Included', type: 'number', editable: true },
      { key: 'extra_location_price', label: 'Extra Location (₹/mo)', type: 'currency', editable: true },
    ]
  },
  {
    group: 'Features',
    icon: Zap,
    features: [
      { key: 'product_addons_enabled', label: 'Product Add-ons', type: 'boolean', editable: true },
      { key: 'variants_enabled', label: 'Variants', type: 'boolean', editable: true },
      { key: 'events_ticketing_enabled', label: 'Events & Ticketing', type: 'boolean', editable: true },
      { key: 'domestic_shipping', label: 'Domestic Shipping', type: 'boolean', editable: true },
      { key: 'international_shipping', label: 'International Shipping', type: 'boolean', editable: true },
    ]
  },
  {
    group: 'Communications',
    icon: Zap,
    features: [
      { key: 'whatsapp_transactional', label: 'WhatsApp Transactional', type: 'status', editable: true },
      { key: 'whatsapp_promotional', label: 'WhatsApp Promotional', type: 'status', editable: true },
      { key: 'whatsapp_crm_price', label: 'WhatsApp CRM (₹/mo)', type: 'currency', editable: true },
      { key: 'sms_marketing', label: 'SMS Marketing', type: 'status', editable: true },
      { key: 'email_marketing', label: 'Email Marketing', type: 'status', editable: true },
      { key: 'email_sms_marketing_price', label: 'Email/SMS Price (₹/mo)', type: 'currency', editable: true },
    ]
  },
  {
    group: 'Integrations',
    icon: Zap,
    features: [
      { key: 'catalogue_pull', label: 'Catalogue Pull', type: 'status', editable: true },
      { key: 'catalogue_pull_price', label: 'Catalogue Pull (₹)', type: 'currency', editable: true },
      { key: 'catalogue_pull_listings', label: 'Catalogue Pull Listings', type: 'number', editable: true },
      { key: 'inventory_sync_price', label: 'Inventory Sync (₹/mo)', type: 'currency', editable: true },
      { key: 'order_sync_price', label: 'Order Sync (₹/mo)', type: 'currency', editable: true },
      { key: 'master_catalogue_access', label: 'Master Catalogue', type: 'status', editable: true },
    ]
  },
  {
    group: 'Support',
    icon: Users,
    features: [
      { key: 'support_level', label: 'Support Level', type: 'select', options: ['email', 'email_chat', 'call', 'priority'], editable: true },
    ]
  },
  {
    group: 'Trial',
    icon: Clock,
    features: [
      { key: 'trial_enabled', label: 'Trial Enabled', type: 'boolean', editable: true },
      { key: 'trial_period_days', label: 'Trial Days', type: 'number', editable: true },
      { key: 'trial_extension_days', label: 'Extension Days', type: 'number', editable: true },
    ]
  },
];

const SubscriptionPlansAdmin = () => {
  const { apiCall } = useAuth();
  const { toast } = useToast();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedPlans, setEditedPlans] = useState({});
  const [activeTab, setActiveTab] = useState('comparison');
  
  // Import/Export states
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/subscription-plans/');
      setPlans(data.sort((a, b) => a.display_order - b.display_order));
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load subscription plans',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // ==================== IMPORT/EXPORT ====================
  
  const handleExportPlans = async () => {
    try {
      const data = await apiCall('/api/subscription-plans/export/json');
      
      // Create downloadable file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `subscription_plans_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast({
        title: 'Export Successful',
        description: `Exported ${data.total_plans} plans and ${data.total_addons} add-ons`
      });
    } catch (error) {
      toast({
        title: 'Export Failed',
        description: error.message || 'Failed to export plans',
        variant: 'destructive'
      });
    }
  };
  
  const handleImportPlans = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      // Validate JSON structure
      let plans = [];
      if (Array.isArray(data)) {
        plans = data;
      } else if (data.plans && Array.isArray(data.plans)) {
        plans = data.plans;
      } else {
        throw new Error('Invalid JSON format. Expected an array of plans or an object with "plans" array.');
      }
      
      if (plans.length === 0) {
        throw new Error('No plans found in the file');
      }
      
      // Send to backend
      const result = await apiCall('/api/subscription-plans/import/json', {
        method: 'POST',
        body: JSON.stringify({
          plans: plans,
          merge_strategy: 'upsert'
        })
      });
      
      toast({
        title: 'Import Successful',
        description: result.message
      });
      
      // Refresh plans list
      fetchPlans();
      
    } catch (error) {
      toast({
        title: 'Import Failed',
        description: error.message || 'Failed to import plans',
        variant: 'destructive'
      });
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleEditValue = (planId, key, value) => {
    setEditedPlans(prev => ({
      ...prev,
      [planId]: {
        ...prev[planId],
        [key]: value
      }
    }));
  };

  const getDisplayValue = (plan, key) => {
    if (editedPlans[plan.id] && editedPlans[plan.id][key] !== undefined) {
      return editedPlans[plan.id][key];
    }
    return plan[key];
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      // For each edited plan, save changes
      for (const [planId, changes] of Object.entries(editedPlans)) {
        if (Object.keys(changes).length > 0) {
          const plan = plans.find(p => p.id === planId);
          if (plan && plan.status !== 'published') {
            await apiCall(`/api/subscription-plans/${planId}`, {
              method: 'PUT',
              body: JSON.stringify(changes)
            });
          }
        }
      }
      toast({ title: 'Success', description: 'Plans updated successfully' });
      setEditedPlans({});
      setEditMode(false);
      fetchPlans();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save plans',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (planId, shouldPublish) => {
    try {
      await apiCall(`/api/subscription-plans/${planId}/publish`, {
        method: 'POST',
        body: JSON.stringify({ publish: shouldPublish })
      });
      toast({
        title: 'Success',
        description: `Plan ${shouldPublish ? 'published' : 'unpublished'} successfully`
      });
      fetchPlans();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const handleCreatePlan = async () => {
    const newPlanCode = `plan_${Date.now()}`;
    try {
      await apiCall('/api/subscription-plans/', {
        method: 'POST',
        body: JSON.stringify({
          plan_name: 'New Plan',
          plan_code: newPlanCode,
          description: 'New subscription plan',
          monthly_price: 0,
          annual_price: 0,
          commission_percentage: 15,
          display_order: plans.length
        })
      });
      toast({ title: 'Success', description: 'New plan created' });
      fetchPlans();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const formatCellValue = (value, feature) => {
    if (value === null || value === undefined) return '-';
    
    if (feature.customText && feature.customText[String(value)]) {
      return feature.customText[String(value)];
    }
    
    switch (feature.type) {
      case 'currency':
        return value === -1 ? 'Custom' : `₹${value.toLocaleString('en-IN')}`;
      case 'number':
        if (value === -1) return 'Custom';
        if (value === 0 && feature.key.includes('max')) return 'Unlimited';
        return `${value}${feature.suffix || ''}`;
      case 'boolean':
        return value ? '✓' : '✗';
      case 'status':
        return value === 'included' ? '✓ Included' : value === 'add_on' ? 'Add-on' : '✗';
      case 'select':
        const strValue = String(value || '');
        return strValue ? strValue.charAt(0).toUpperCase() + strValue.slice(1).replace('_', ' ') : '-';
      default:
        return String(value);
    }
  };

  const renderEditableCell = (plan, feature) => {
    const value = getDisplayValue(plan, feature.key);
    const isEditable = feature.editable && plan.status !== 'published';

    if (!editMode || !isEditable) {
      return (
        <span className={`${plan.status === 'published' ? 'font-medium' : ''}`}>
          {formatCellValue(value, feature)}
        </span>
      );
    }

    switch (feature.type) {
      case 'boolean':
        return (
          <Switch
            checked={value}
            onCheckedChange={(checked) => handleEditValue(plan.id, feature.key, checked)}
          />
        );
      case 'select':
        return (
          <Select
            value={value}
            onValueChange={(val) => handleEditValue(plan.id, feature.key, val)}
          >
            <SelectTrigger className="h-8 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {feature.options.map(opt => (
                <SelectItem key={opt} value={opt}>
                  {String(opt).charAt(0).toUpperCase() + String(opt).slice(1).replace('_', ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'status':
        return (
          <Select
            value={value}
            onValueChange={(val) => handleEditValue(plan.id, feature.key, val)}
          >
            <SelectTrigger className="h-8 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="included">Included</SelectItem>
              <SelectItem value="add_on">Add-on</SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
            </SelectContent>
          </Select>
        );
      default:
        return (
          <Input
            type="number"
            className="h-8 w-full"
            value={value ?? ''}
            onChange={(e) => handleEditValue(plan.id, feature.key, parseFloat(e.target.value) || 0)}
          />
        );
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Subscription Plans</h1>
            <p className="text-muted-foreground">Manage vendor subscription tiers, custom plans, and trial settings</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="standard" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-flex">
            <TabsTrigger value="standard" className="flex items-center gap-2" data-testid="standard-plans-tab">
              <Crown className="h-4 w-4" />
              Standard Plans
            </TabsTrigger>
            <TabsTrigger value="custom" className="flex items-center gap-2" data-testid="custom-plans-tab">
              <Building2 className="h-4 w-4" />
              Custom Plans
            </TabsTrigger>
            <TabsTrigger value="trial" className="flex items-center gap-2" data-testid="trial-settings-tab">
              <Clock className="h-4 w-4" />
              Trial Settings
            </TabsTrigger>
          </TabsList>

          {/* Standard Plans Tab */}
          <TabsContent value="standard" className="space-y-6">
            {/* Edit Controls */}
            {/* Edit Controls */}
            <div className="flex justify-end gap-2">
              {/* Hidden file input for import */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleImportPlans}
                className="hidden"
                data-testid="import-plans-file-input"
              />
              
              {editMode ? (
                <>
                  <Button variant="outline" onClick={() => { setEditMode(false); setEditedPlans({}); }}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveAll} disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Save All Changes
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={importing}
                    data-testid="import-plans-btn"
                  >
                    {importing ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <FileUp className="h-4 w-4 mr-2" />
                    )}
                    Import Plans
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={handleExportPlans}
                    data-testid="export-plans-btn"
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    Export Plans
                  </Button>
                  <Button variant="outline" onClick={() => setEditMode(true)}>
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Plans
                  </Button>
                  <Button onClick={handleCreatePlan}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Plan
                  </Button>
                </>
              )}
            </div>

            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-900">GST @18% applies to all subscription fees</p>
                <p className="text-blue-700">Published plans cannot be edited. Unpublish first to make changes.</p>
              </div>
            </div>

            {/* Comparison Table */}
            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left py-3 px-4 font-semibold w-64 sticky left-0 bg-muted/50">Feature</th>
                      {plans.map(plan => (
                        <th key={plan.id} className="text-center py-3 px-4 min-w-[180px]">
                          <div className="space-y-1">
                            {editMode && plan.status !== 'published' ? (
                              <Input
                                className="h-8 text-center font-semibold"
                                value={getDisplayValue(plan, 'plan_name')}
                                onChange={(e) => handleEditValue(plan.id, 'plan_name', e.target.value)}
                              />
                            ) : (
                              <span className="font-semibold">{plan.plan_name}</span>
                            )}
                            <div className="flex justify-center gap-1">
                              <Badge variant={plan.status === 'published' ? 'default' : 'outline'} className="text-xs">
                                {plan.status}
                              </Badge>
                              {plan.is_featured && <Badge className="bg-amber-500 text-xs">Featured</Badge>}
                            </div>
                            {!editMode && (
                              <div className="flex justify-center gap-1 pt-1">
                                {plan.status === 'draft' && (
                                  <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => handlePublish(plan.id, true)}>
                                    <Eye className="h-3 w-3 mr-1" />Publish
                                  </Button>
                            )}
                            {plan.status === 'published' && (
                              <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => handlePublish(plan.id, false)}>
                                <EyeOff className="h-3 w-3 mr-1" />Unpublish
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FEATURE_GROUPS.map((group, gIndex) => (
                  <React.Fragment key={group.group}>
                    {/* Group Header */}
                    <tr className="bg-muted/30">
                      <td colSpan={plans.length + 1} className="py-2 px-4">
                        <div className="flex items-center gap-2 font-semibold text-xs uppercase text-muted-foreground">
                          <group.icon className="h-4 w-4" />
                          {group.group}
                        </div>
                      </td>
                    </tr>
                    {/* Feature Rows */}
                    {group.features.map((feature, fIndex) => (
                      <tr key={feature.key} className="border-b border-muted/20 hover:bg-muted/10">
                        <td className="py-2 px-4 text-muted-foreground sticky left-0 bg-white">
                          {feature.label}
                        </td>
                        {plans.map(plan => (
                          <td key={plan.id} className="py-2 px-4 text-center">
                            {renderEditableCell(plan, feature)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Plans Count */}
        <div className="text-sm text-muted-foreground">
          {plans.length} plan(s) • {plans.filter(p => p.status === 'published').length} published
        </div>
          </TabsContent>

          {/* Custom Plans Tab */}
          <TabsContent value="custom">
            <CustomPlansTab />
          </TabsContent>

          {/* Trial Settings Tab */}
          <TabsContent value="trial">
            <TrialSettingsTab />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default SubscriptionPlansAdmin;
